export interface HrmModuleFeature {
  title: string;
  description?: string;
  items?: string[];
}

export interface HrmModule {
  id: string;
  title: string;
  description: string;
  features: HrmModuleFeature[];
}

export interface HrmWorkflowStep {
  title: string;
  text: string;
}

export interface HrmUserRole {
  title: string;
  intro: string;
  items: string[];
}

export interface HrmPanelSection {
  id: string;
  title: string;
  description: string;
  placeholderLabel: string;
  placeholderHint: string;
  featuresLeft: string[];
  featuresRight: string[];
  imagePosition: "left" | "right";
}

export const hrmAdvantages = [
  "Centralized employee database with complete profiles, documents, and employment history",
  "Automated payroll processing with tax deductions, allowances, and payslip generation",
  "Real-time attendance tracking with shift management and biometric integration support",
  "Self-service employee portal for leave requests, payslips, and profile updates",
  "End-to-end recruitment pipeline — job posting, applicant tracking, and interview scheduling",
  "Performance appraisal workflows with KPI tracking, goals, and feedback cycles",
  "Compliance-ready statutory reports for PF, ESI, TDS, and labour law requirements",
  "Multi-branch and multi-department support with role-based access control",
  "Leave management with approval hierarchies, encashment, and balance tracking",
  "Insightful HR analytics dashboards for workforce planning and decision-making",
];

export const hrmWorkflowSteps: HrmWorkflowStep[] = [
  {
    title: "Employee Onboarding",
    text: "HR creates employee profiles, assigns departments and roles, uploads documents, and configures access to the self-service portal.",
  },
  {
    title: "Attendance & Shifts",
    text: "Employees clock in/out via web or mobile. The system tracks shifts, overtime, and late arrivals automatically.",
  },
  {
    title: "Leave Management",
    text: "Staff submit leave requests through the portal. Managers and HR approve or reject with real-time balance updates.",
  },
  {
    title: "Payroll Processing",
    text: "Salary structures, deductions, and bonuses are calculated automatically. Payslips are generated and distributed digitally.",
  },
  {
    title: "Performance Review",
    text: "Managers set goals, conduct appraisals, and record feedback. HR monitors review cycles and performance trends.",
  },
  {
    title: "Reports & Compliance",
    text: "Admin generates workforce, payroll, attendance, and statutory compliance reports from a unified dashboard.",
  },
];

export const hrmUserRoles: HrmUserRole[] = [
  {
    title: "Admin",
    intro: "Full system control — configure company settings, manage users, and oversee all HR operations.",
    items: [
      "Company & branch setup",
      "User role management",
      "Department configuration",
      "Policy & holiday calendar",
      "System settings & audit logs",
      "Compliance report generation",
    ],
  },
  {
    title: "HR Manager",
    intro: "Manage the complete employee lifecycle from hiring to exit with centralized HR tools.",
    items: [
      "Employee records & documents",
      "Recruitment & onboarding",
      "Payroll configuration",
      "Leave policy management",
      "Performance review cycles",
      "HR analytics & reports",
    ],
  },
  {
    title: "Department Manager",
    intro: "Oversee team attendance, approve leave, and conduct performance reviews for direct reports.",
    items: [
      "Team attendance overview",
      "Leave approval workflow",
      "Shift scheduling",
      "Performance appraisals",
      "Team reports",
      "Goal assignment & tracking",
    ],
  },
  {
    title: "Employee",
    intro: "Self-service access to personal HR information, requests, and company updates.",
    items: [
      "Profile & document access",
      "Attendance & shift view",
      "Leave request & balance",
      "Payslip download",
      "Performance goals & feedback",
      "Company announcements",
    ],
  },
];

export const hrmModules: HrmModule[] = [
  {
    id: "employee",
    title: "Employee Management",
    description:
      "Complete employee lifecycle management — from onboarding and profile management to exit processing.",
    features: [
      {
        title: "Employee Profiles",
        description: "Store personal details, contact info, emergency contacts, and employment history.",
        items: [
          "Personal & contact details",
          "Job title & department",
          "Employment type & joining date",
          "Reporting manager",
          "Document uploads",
        ],
      },
      {
        title: "Onboarding",
        description: "Structured onboarding workflows with checklist tasks and document collection.",
      },
      {
        title: "Organization Chart",
        description: "Visual hierarchy showing departments, teams, and reporting structures.",
      },
      {
        title: "Exit Management",
        description: "Handle resignations, clearance workflows, and full-and-final settlement tracking.",
      },
    ],
  },
  {
    id: "attendance",
    title: "Attendance & Leave",
    description:
      "Track attendance, manage shifts, and handle leave requests with automated approval workflows.",
    features: [
      {
        title: "Attendance Tracking",
        description: "Clock in/out, biometric integration support, and late/early departure alerts.",
        items: ["Web & mobile check-in", "Shift-wise tracking", "Overtime calculation", "Attendance reports"],
      },
      {
        title: "Shift Management",
        description: "Define shift patterns, assign rosters, and manage shift swaps.",
      },
      {
        title: "Leave Management",
        description: "Configure leave types, balances, encashment rules, and multi-level approvals.",
        items: ["Casual, sick & earned leave", "Leave balance tracking", "Approval hierarchy", "Holiday calendar"],
      },
    ],
  },
  {
    id: "payroll",
    title: "Payroll & Compensation",
    description:
      "Automated salary processing with configurable structures, deductions, and statutory compliance.",
    features: [
      {
        title: "Salary Structure",
        description: "Define components — basic, HRA, allowances, bonuses, and variable pay.",
      },
      {
        title: "Payroll Processing",
        description: "Automated monthly payroll with attendance-linked calculations and arrears handling.",
        items: ["Payslip generation", "Tax & PF deductions", "Bonus & incentive processing", "Bank transfer reports"],
      },
      {
        title: "Statutory Compliance",
        description: "PF, ESI, TDS, and professional tax reports generated automatically.",
      },
    ],
  },
  {
    id: "recruitment",
    title: "Recruitment",
    description:
      "Manage the full hiring pipeline — from job posting and applicant tracking to offer letters.",
    features: [
      {
        title: "Job Posting",
        description: "Create and publish job openings with role descriptions and requirements.",
      },
      {
        title: "Applicant Tracking",
        description: "Track candidates through screening, interviews, and selection stages.",
        items: ["Resume management", "Interview scheduling", "Candidate scoring", "Offer letter generation"],
      },
      {
        title: "Onboarding Link",
        description: "Seamlessly convert selected candidates into employee records.",
      },
    ],
  },
  {
    id: "performance",
    title: "Performance Management",
    description:
      "Set goals, conduct appraisals, and track employee performance across review cycles.",
    features: [
      {
        title: "Goal Setting",
        description: "Define individual and team KPIs aligned with company objectives.",
      },
      {
        title: "Appraisal Cycles",
        description: "Configure review periods, self-assessments, and manager evaluations.",
        items: ["360-degree feedback", "Rating scales", "Review history", "Increment recommendations"],
      },
      {
        title: "Training & Development",
        description: "Track skill gaps, training programs, and certification records.",
      },
    ],
  },
  {
    id: "reports",
    title: "Reports & Analytics",
    description:
      "Comprehensive HR dashboards and exportable reports for data-driven workforce decisions.",
    features: [
      {
        title: "HR Dashboard",
        description: "Real-time overview of headcount, attendance, leave, and payroll metrics.",
      },
      {
        title: "Workforce Reports",
        description: "Employee demographics, turnover, department-wise strength, and hiring trends.",
      },
      {
        title: "Compliance Reports",
        description: "Statutory filings, audit trails, and regulatory compliance documentation.",
      },
    ],
  },
];

export const hrmAdminFeaturesLeft = [
  "Authentication & Role Access",
  "One-Click HR Dashboard",
  "Employee Management",
  "Department & Branch Setup",
  "Attendance Configuration",
  "Leave Policy Management",
  "Payroll Setup",
  "Recruitment Pipeline",
];

export const hrmAdminFeaturesRight = [
  "Shift & Roster Management",
  "Performance Review Cycles",
  "Document Management",
  "Holiday Calendar",
  "Notification & Alerts",
  "Statutory Compliance Reports",
  "Audit Logs",
  "Insights & Analytics",
];

export const hrmEmployeeFeaturesLeft = [
  "Profile Management",
  "Attendance View",
  "Clock In / Clock Out",
  "Leave Request",
  "Leave Balance",
  "Payslip Download",
  "Performance Goals",
  "Company Announcements",
];

export const hrmEmployeeFeaturesRight = [
  "Document Access",
  "Shift Schedule View",
  "Team Directory",
  "Self-Assessment",
  "Feedback & Reviews",
  "Training Records",
  "Help Desk / HR Support",
  "Notification Preferences",
];

export const hrmPanels: HrmPanelSection[] = [
  {
    id: "admin",
    title: "HRM Admin Panel",
    description:
      "A fully-integrated admin panel to manage all HR operations — employees, payroll, attendance, recruitment, and compliance.",
    placeholderLabel: "HR Admin Dashboard",
    placeholderHint: "Headcount · Attendance · Payroll · Reports",
    featuresLeft: hrmAdminFeaturesLeft,
    featuresRight: hrmAdminFeaturesRight,
    imagePosition: "left",
  },
  {
    id: "employee",
    title: "Employee Self-Service Portal",
    description:
      "A user-friendly employee portal for attendance, leave, payslips, performance, and personal profile management.",
    placeholderLabel: "Employee Portal",
    placeholderHint: "Leave · Payslips · Profile · Goals",
    featuresLeft: hrmEmployeeFeaturesLeft,
    featuresRight: hrmEmployeeFeaturesRight,
    imagePosition: "right",
  },
];
